import {
  db,
  stageInWorker,
  syncInWorker,
} from '@/app/db/client';
import { Repository } from '@/app/db/repo';
import { ensureSchema } from '@/app/db/schema';
import {
  biometricsAvailable,
  createPasskey,
  verifyPasskey,
} from '@/app/sync/biometric';
import type { Keycheck } from '@/app/sync/crypto';
import {
  DEFAULT_BRANCH,
  GitHub,
} from '@/app/sync/github';
import {
  dropPasskey,
  forget,
  recall,
  remember,
  savedPasskey,
  savePasskey,
} from '@/app/sync/remembered';
import {
  enrollTotp,
  type KeycheckGate,
  keycheckGate,
  totpSecret,
  unlock,
} from '@/app/sync/session';
import {
  newTotpSecret,
  totpUri,
  verifyTotp,
} from '@/app/sync/totp';
import {
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { renderSVG } from 'uqr';
import { Notes } from './Notes';
import { PasswordInput } from './PasswordInput';
import type { Leave } from './useBackgroundSync';
import { useDailyBackup } from './useDailyBackup';
import type { RunSync } from './useSync';

const repo = new Repository(db);

const allNotes = () => repo.allNotes();

// the sync itself runs in the database's worker, off the page, and so does
// handing it to the service worker when the app is left. Neither happens
// before the remote has confirmed the keycheck the key came from - staging
// encrypts too, for the service worker to push - and one it has replaced
// drops the key and locks (`stale`). With a passkey, the notes lock each time
// the app goes out of view.
const readyWith = (
  github: GitHub,
  key: CryptoKey,
  gate: KeycheckGate,
  stale: () => Promise<void>,
  passkey: string | null,
  locked: boolean,
): State => ({
  screen: 'ready',
  sync: async onProgress => {
    if (!await gate.check()) {
      await stale();
      throw new Error('the passphrase was changed on another device');
    }
    return syncInWorker(github.config, key, onProgress);
  },
  // no request as the app is left: unconfirmed, the changes wait for a sync
  leave: async () => gate.confirmed() ? stageInWorker(key) : 0,
  passkey,
  locked,
});

const saveKeycheck = (keycheck: Keycheck) =>
  repo.setSetting('keycheck', JSON.stringify(keycheck));

// The device's own lock, asked for from here on each time the app comes back
// into view. Set up once; skipped where the device has none, and tried again
// at the next unlock when it's declined.
const ensurePasskey = async (label: string) => {
  try {
    const existing = await savedPasskey();
    if (existing || !await biometricsAvailable()) return existing;
    const id = await createPasskey(label);
    await savePasskey(id);
    return id;
  } catch (e) {
    console.error('no passkey', e);
    return null;
  }
};

const saveSetup = async (repoName: string, token: string, branch: string) => {
  const [lastRepo, lastBranch] = await Promise.all([
    repo.getSetting('repo'),
    repo.getSetting('branch'),
  ]);
  // the notes here go to another repo as new ones, rather than be forgotten
  // for not being on it; the same repo set up again keeps them as they are
  if (lastRepo && (lastRepo !== repoName || lastBranch !== branch)) {
    await repo.forgetRemote();
  }
  await repo.setSetting('repo', repoName);
  await repo.setSetting('token', token);
  await repo.setSetting('branch', branch);
  // belongs to whichever repo was set up before
  await repo.deleteSetting('keycheck');
  await forget();
};

// `yarn dev` only: the repo and token come from the secrets file, re-saved
// only when they changed so the cached keycheck survives
const devSetup = async (
  { REPO, GITHUB_TOKEN, BRANCH = DEFAULT_BRANCH }: NonNullable<
    typeof __DEV_SECRETS__
  >,
) => {
  if (!REPO || !GITHUB_TOKEN) return;
  const [repoName, token, branch] = await Promise.all([
    repo.getSetting('repo'),
    repo.getSetting('token'),
    repo.getSetting('branch'),
  ]);
  if (repoName === REPO && branch === BRANCH) {
    if (token !== GITHUB_TOKEN) await repo.setSetting('token', GITHUB_TOKEN);
  } else {
    await saveSetup(REPO, GITHUB_TOKEN, BRANCH);
  }
};

const cachedKeycheck = async () => {
  const cached = await repo.getSetting('keycheck');
  return cached ? JSON.parse(cached) as Keycheck : null;
};

// Past the passphrase: the key, and the authenticator secret still to be
// checked, or a fresh one to set up when the repo has none yet
type Passed = {
  github: GitHub,
  repoName: string,
  key: CryptoKey,
  keycheck: Keycheck,
  secret: string,
  enrolling: boolean,
};

// null on a wrong passphrase
const tryPassphrase = async (
  github: GitHub,
  repoName: string,
  passphrase: string,
): Promise<Passed | null> => {
  const result = await unlock(github, passphrase, await cachedKeycheck());
  if (!result.ok) return null;
  const { key, keycheck } = result;
  await saveKeycheck(keycheck);
  const secret = await totpSecret(key, keycheck);
  return {
    github,
    repoName,
    key,
    keycheck,
    secret: secret ?? newTotpSecret(),
    enrolling: secret === null,
  };
};

// ready to sync on the right code, null on a wrong one
const tryCode = async (
  passed: Passed,
  code: string,
  stale: () => Promise<void>,
) => {
  if (!await verifyTotp(passed.secret, code)) return null;
  const keycheck = passed.enrolling
    ? await enrollTotp(
      passed.github,
      passed.key,
      passed.keycheck,
      passed.secret,
    )
    : passed.keycheck;
  await saveKeycheck(keycheck);
  await remember(passed.key, keycheck);
  const passkey = await ensurePasskey(passed.repoName);
  return readyWith(
    passed.github,
    passed.key,
    keycheckGate(passed.github, keycheck, saveKeycheck),
    stale,
    passkey,
    false,
  );
};

type State =
  | { screen: 'loading' }
  | { screen: 'setup' }
  | { screen: 'locked', github: GitHub, repoName: string }
  | { screen: 'code', passed: Passed }
  | {
    screen: 'ready',
    sync: RunSync,
    leave: Leave,
    passkey: string | null,
    locked: boolean,
  }
  | { screen: 'failed', error: string };

export const App = () => {
  const [state, setState] = useState<State>({ screen: 'loading' });

  const load = async () => {
    await ensureSchema(db);
    if (__DEV_SECRETS__) await devSetup(__DEV_SECRETS__);
    const [repoName, token, branch] = await Promise.all([
      repo.getSetting('repo'),
      repo.getSetting('token'),
      repo.getSetting('branch'),
    ]);
    if (!repoName || !token) return setState({ screen: 'setup' });
    const github = new GitHub({
      repo: repoName,
      token,
      branch: branch ?? DEFAULT_BRANCH,
    });
    // opens on the cached keycheck at once; the remote's is checked before
    // the first sync, and a key it has replaced dropped then (readyWith)
    const keycheck = await cachedKeycheck();
    const key = await recall(keycheck);
    if (key && keycheck) {
      const passkey = await savedPasskey();
      return setState(
        readyWith(
          github,
          key,
          keycheckGate(github, keycheck, saveKeycheck),
          stale,
          passkey,
          passkey !== null,
        ),
      );
    }
    const label = `${repoName} · ${branch ?? DEFAULT_BRANCH}`;
    // `yarn dev` fills in the passphrase, never the code
    const passphrase = __DEV_SECRETS__?.PASSPHRASE;
    const passed = passphrase
      ? await tryPassphrase(github, label, passphrase)
      : null;
    setState(
      passed
        ? { screen: 'code', passed }
        : { screen: 'locked', github, repoName: label },
    );
  };

  // back to the passphrase and code, with a passkey set up afresh after
  // them: for one that stopped working, and to sign out. The notes stay on
  // the device and sync after the next unlock.
  // the passphrase changed elsewhere: the key goes, the passkey stays
  const stale = async () => {
    await forget();
    await load();
  };

  const forgetDevice = async () => {
    await forget();
    await dropPasskey();
    await load();
  };

  useEffect(() => {
    load().catch(e => setState({ screen: 'failed', error: String(e) }));
  }, []);

  switch (state.screen) {
    case 'loading':
      return (
        <div className="loader" role="status" aria-label="Loading">
          <span />
          <span />
          <span />
        </div>
      );
    case 'failed':
      return <div className="centered error">{state.error}</div>;
    case 'setup':
      return (
        <Setup
          onDone={async (repoName, token, branch) => {
            await saveSetup(repoName, token, branch);
            await load();
          }} />
      );
    case 'locked':
      return (
        <Unlock
          repoName={state.repoName}
          onUnlock={async passphrase => {
            const passed = await tryPassphrase(
              state.github,
              state.repoName,
              passphrase,
            );
            if (passed) setState({ screen: 'code', passed });
            return passed !== null;
          }}
          onReset={() => setState({ screen: 'setup' })} />
      );
    case 'code':
      return (
        <SecondFactor
          passed={state.passed}
          onCode={async code => {
            const ready = await tryCode(state.passed, code, stale);
            if (ready) setState(ready);
            return ready !== null;
          }}
          onBack={() =>
            setState({
              screen: 'locked',
              github: state.passed.github,
              repoName: state.passed.repoName,
            })} />
      );
    case 'ready':
      return (
        <Ready
          sync={state.sync}
          leave={state.leave}
          passkey={state.passkey}
          locked={state.locked}
          onPassphrase={forgetDevice}
          onSignOut={forgetDevice} />
      );
  }
};

// Long enough to go copy something in another app and come back to paste it.
const GRACE_MS = 30_000;

// The notes stay mounted under the lock, so an edit in progress and the
// open note are there as they were once it's lifted. The lock goes up as soon
// as the app is out of view, so the app switcher never shows a note, but
// coming back within the grace lifts it without asking.
const Ready = (
  { sync, leave, passkey, locked: startLocked, onPassphrase, onSignOut }: {
    sync: RunSync,
    leave: Leave,
    passkey: string | null,
    locked: boolean,
    onPassphrase: () => Promise<void>,
    onSignOut: () => Promise<void>,
  },
) => {
  const [locked, setLocked] = useState(startLocked);
  const lockedNow = useRef(locked);
  lockedNow.current = locked;
  // only leaving unlocked earns a grace; a lock already up stays up
  const graceUntil = useRef(0);
  const inGrace = useCallback(() => Date.now() < graceUntil.current, []);
  const unlocked = useCallback(() => setLocked(false), []);
  useDailyBackup(allNotes);

  useEffect(() => {
    if (!passkey) return;
    const onHidden = () => {
      if (document.visibilityState !== 'hidden') return;
      graceUntil.current = lockedNow.current ? 0 : Date.now() + GRACE_MS;
      setLocked(true);
      // or the keyboard stays up over the lock
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };
    document.addEventListener('visibilitychange', onHidden);
    return () => document.removeEventListener('visibilitychange', onHidden);
  }, [passkey]);

  return (
    <>
      <div className="lockable" inert={locked} aria-hidden={locked}>
        <Notes
          repo={repo}
          sync={sync}
          leave={leave}
          onSignOut={onSignOut} />
      </div>
      {locked && passkey && (
        <Lock
          passkey={passkey}
          inGrace={inGrace}
          onUnlocked={unlocked}
          onPassphrase={onPassphrase} />
      )}
    </>
  );
};

// Asks by itself once each time the app comes into view; a cancelled prompt
// then waits for the button, rather than coming straight back. Hiding the
// page while the prompt is up (Android can) doesn't count as leaving. Back
// within the grace, it lifts without asking.
const Lock = (
  { passkey, inGrace, onUnlocked, onPassphrase }: {
    passkey: string,
    inGrace: () => boolean,
    onUnlocked: () => void,
    onPassphrase: () => Promise<void>,
  },
) => {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const asking = useRef(false);
  const due = useRef(true);

  const ask = useCallback(async () => {
    if (asking.current) return;
    asking.current = true;
    due.current = false;
    setBusy(true);
    setError(null);
    try {
      if (await verifyPasskey(passkey)) onUnlocked();
      else setError('Not verified');
    } catch (e) {
      // cancelled, timed out, or asked without a tap first (iOS)
      if (!(e instanceof DOMException && e.name === 'NotAllowedError')) {
        setError(String(e));
      }
    } finally {
      asking.current = false;
      setBusy(false);
    }
  }, [passkey, onUnlocked]);

  useEffect(() => {
    const onChange = () => {
      if (document.visibilityState === 'hidden') {
        if (!asking.current) due.current = true;
      } else if (inGrace()) {
        onUnlocked();
      } else if (due.current) {
        ask();
      }
    };
    onChange();
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, [ask, inGrace, onUnlocked]);

  return (
    <div className="lock">
      <div className="centered form">
        <h1>Notes</h1>
        <p className="muted">Locked</p>
        <button
          type="button"
          className="primary"
          autoFocus
          disabled={busy}
          onClick={ask}>
          {busy ? 'Unlocking…' : 'Unlock'}
        </button>
        {error && <p className="error" role="alert">{error}</p>}
        <button type="button" className="link" onClick={onPassphrase}>
          Use the passphrase instead
        </button>
      </div>
    </div>
  );
};

const Setup = (
  { onDone }: {
    onDone: (repo: string, token: string, branch: string) => Promise<void>,
  },
) => {
  const [repoName, setRepoName] = useState('');
  const [token, setToken] = useState('');
  const [branch, setBranch] = useState(DEFAULT_BRANCH);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    onDone(repoName.trim(), token.trim(), branch.trim());
  };

  return (
    <form className="centered form" onSubmit={submit}>
      <h1>Notes</h1>
      <p className="muted">
        A private GitHub repo to sync into, and a fine-grained token with
        read/write <em>Contents</em>{' '}
        access to just that repo. Notes go on their own branch, created if it
        doesn't exist.
      </p>
      <input
        placeholder="owner/repo"
        aria-label="Repository"
        value={repoName}
        onChange={e => setRepoName(e.target.value)}
        required
        pattern="[^\/\s]+\/[^\/\s]+" />
      <input
        placeholder="branch"
        aria-label="Branch"
        value={branch}
        onChange={e => setBranch(e.target.value)}
        required />
      <PasswordInput
        placeholder="github_pat_…"
        aria-label="Token"
        value={token}
        onChange={e => setToken(e.target.value)}
        required />
      <button type="submit" className="primary">Continue</button>
    </form>
  );
};

const Unlock = (
  { repoName, onUnlock, onReset }: {
    repoName: string,
    onUnlock: (passphrase: string) => Promise<boolean>,
    onReset: () => void,
  },
) => {
  const [passphrase, setPassphrase] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (!await onUnlock(passphrase)) setError('Wrong passphrase');
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="centered form" onSubmit={submit}>
      <h1>Notes</h1>
      <p className="muted">{repoName}</p>
      <PasswordInput
        autoFocus
        placeholder="Passphrase"
        aria-label="Passphrase"
        value={passphrase}
        onChange={e => setPassphrase(e.target.value)}
        required />
      <button type="submit" className="primary" disabled={busy}>
        {busy ? 'Unlocking…' : 'Unlock'}
      </button>
      {error && <p className="error" role="alert">{error}</p>}
      <button type="button" className="link" onClick={onReset}>
        Use a different repo
      </button>
    </form>
  );
};

const SecondFactor = (
  { passed, onCode, onBack }: {
    passed: Passed,
    onCode: (code: string) => Promise<boolean>,
    onBack: () => void,
  },
) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const qr = useMemo(
    () =>
      passed.enrolling
        ? renderSVG(totpUri(passed.secret, passed.repoName), { border: 2 })
        : null,
    [passed],
  );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (!await onCode(code)) setError('Wrong code');
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="centered form" onSubmit={submit}>
      <h1>Notes</h1>
      {qr
        ? (
          <>
            <p className="muted">
              Scan this with an authenticator app, then enter the code it shows.
              A device asks for a code from it again after 5 days unopened.
            </p>
            <div
              className="totp-qr"
              role="img"
              aria-label="Authenticator QR code"
              dangerouslySetInnerHTML={{ __html: qr }} />
            <code className="totp-secret">{passed.secret}</code>
          </>
        )
        : <p className="muted">The code from your authenticator app.</p>}
      <input
        autoFocus
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="123456"
        aria-label="Authenticator code"
        value={code}
        onChange={e => setCode(e.target.value)}
        required
        pattern="\s*\d{3}\s?\d{3}\s*" />
      <button type="submit" className="primary" disabled={busy}>
        {busy ? 'Checking…' : 'Verify'}
      </button>
      {error && <p className="error" role="alert">{error}</p>}
      <button type="button" className="link" onClick={onBack}>
        Back
      </button>
    </form>
  );
};
